// Politica de movilidad de un legajo editable desde la consola de UCORP
// (AT-17, docs/PLAN-POLITICA-PASAJERO.md). Hasta aca entraba solo por
// manifiesto; regla 10: todo lo configurable tiene CRUD con auditoria.
//
// La forma replica mobilityPolicySchema de @ucorp/tenant (contracts es
// standalone y no importa tenant). La API vuelve a validar con el schema del
// manifiesto y contra los proveedores y lineas de la planta: una politica
// invalida romperia el pedido. apps/api verifica en un test que las dos
// formas acepten y rechacen lo mismo.
//
// Scope /admin: ucorp_admin escribe, ucorp_admin_reader lee. Sin nombre,
// telefono ni domicilio: la consola busca el legajo por su codigo.
import { getRefId } from "@asteasolutions/zod-to-openapi";
import { z } from "zod";
import { ProviderId, RecordId, SiteId, SiteParams } from "./common.js";
import { MobileRideRequestService } from "./mobile-trips.js";
const BusLine = z.string().min(1).openapi({ description: "Id de la linea de colectivo de la planta." });
export const RecordShuttleMode = z
    .object({
    type: z.literal("shuttle"),
    provider: ProviderId.optional().openapi({
        description: "Proveedor de combi del legajo. Ausente: el proveedor por defecto de la planta o el unico que presta combi.",
    }),
})
    .strict()
    .openapi("RecordShuttleMode", { description: "Combi puerta a planta segun el armado." });
export const RecordBusMode = z
    .object({ type: z.literal("bus"), line: BusLine })
    .strict()
    .openapi("RecordBusMode", { description: "Linea de colectivo con reserva. No se pide desde la app." });
export const RecordCabMode = z
    .object({ type: z.literal("cab") })
    .strict()
    .openapi("RecordCabMode", { description: "Auto (individual o compartido segun el auto compartido del legajo)." });
export const RecordMobilityMode = z
    .discriminatedUnion("type", [RecordShuttleMode, RecordBusMode, RecordCabMode])
    .openapi("RecordMobilityMode", { description: "Un modo de transporte del legajo." });
export const RecordCombinedPolicy = z
    .object({
    type: z.literal("combined"),
    modes: z.array(RecordMobilityMode).min(2).openapi({ description: "Modos habilitados, en orden de preferencia. Al menos dos." }),
})
    .strict()
    .openapi("RecordCombinedPolicy", { description: "Mas de un modo, por ejemplo auto y combi." });
export const RecordMobilityPolicy = z
    .discriminatedUnion("type", [RecordShuttleMode, RecordBusMode, RecordCabMode, RecordCombinedPolicy])
    .openapi("RecordMobilityPolicy", {
    description: "Politica de movilidad del legajo, con la misma forma que people[].mobilityPolicy del manifiesto.",
});
export const RecordMobilityPolicyParams = SiteParams.extend({ record: RecordId }).openapi("RecordMobilityPolicyParams", {
    description: "Planta y legajo.",
});
export const RecordMobilityPolicyChoices = z
    .object({
    shuttleProviders: z.array(ProviderId).openapi({ description: "Proveedores activos de la planta que prestan combi." }),
    busLines: z.array(BusLine).openapi({ description: "Lineas de colectivo de la planta." }),
})
    .strict()
    .openapi("RecordMobilityPolicyChoices", { description: "Lo que la politica puede nombrar en esta planta." });
// zod-to-openapi 9 pierde el null al anular un union con nombre (no tiene un
// `type` al que sumarle null): se declara el oneOf con null para que el wire
// diga lo mismo que valida el schema.
const nullableRecordMobilityPolicyOneOf = [
    ...RecordMobilityPolicy.options.map((option) => ({ $ref: `#/components/schemas/${getRefId(option) ?? ""}` })),
    { type: "null" },
];
export const RecordMobilityPolicyResponse = z
    .object({
    siteId: SiteId,
    record: RecordId,
    mobilityPolicy: RecordMobilityPolicy.nullable().openapi({
        oneOf: nullableRecordMobilityPolicyOneOf,
        description: "La politica guardada. null: la guardada no es una politica valida y rige la de la planta (fromSiteDefault).",
    }),
    fromSiteDefault: z.boolean().openapi({ description: "true si el legajo no tiene politica valida y toma la de la planta (su modalidad por defecto, defaultMobilityMode)." }),
    services: z.array(MobileRideRequestService).openapi({
        description: "Lo que la app le deja pedir desde la proxima lectura, en orden de preferencia. Vacia: solo colectivo.",
    }),
    choices: RecordMobilityPolicyChoices,
})
    .openapi("RecordMobilityPolicyResponse", { description: "Politica de movilidad de un legajo y la efectiva, sin datos personales." });
export const UpdateRecordMobilityPolicyRequest = z
    .object({ mobilityPolicy: RecordMobilityPolicy })
    .strict()
    .openapi("UpdateRecordMobilityPolicyRequest", {
    description: "Reemplaza la politica del legajo. Proveedor o linea que no son de la planta: 400. Auditado con antes y despues; rige en la proxima lectura de la app, el proximo pedido y el proximo armado.",
});
//# sourceMappingURL=record-policy.js.map